import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useThreads, useCreateThread, Thread } from "@/hooks/useThreads";
import { useApplications } from "@/hooks/useApplication";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquarePlus, MessageCircle, Loader2, Clock, CheckCircle, AlertCircle } from "lucide-react";
import ThreadView, { type ThreadViewDraft } from "./ThreadView";

export interface ThreadListState {
  showNew: boolean;
  selectedThread: Thread | null;
  newSubject: string;
  newBody: string;
  selectedAppId: string;
  drafts: Record<string, ThreadViewDraft>;
}
export const emptyThreadListState: ThreadListState = {
  showNew: false, selectedThread: null, newSubject: "", newBody: "", selectedAppId: "", drafts: {},
};

interface Props {
  applicationId?: string;
  initialThreadId?: string;
  hideHeading?: boolean;
  state?: ThreadListState;
  onStateChange?: Dispatch<SetStateAction<ThreadListState>>;
  onThreadOpened?: () => void;
}

const ThreadList = ({ applicationId, initialThreadId, hideHeading = false, state, onStateChange, onThreadOpened }: Props) => {
  const { t } = useTranslation("portal");
  const STATUS_BADGE: Record<string, { icon: typeof Clock; className: string; label: string }> = {
    open: { icon: AlertCircle, className: "text-blue-600 bg-blue-50", label: t("dashboard.status.open") },
    waiting_reply: { icon: Clock, className: "text-yellow-600 bg-yellow-50", label: t("dashboard.status.waiting_reply") },
    closed: { icon: CheckCircle, className: "text-muted-foreground bg-muted", label: t("dashboard.status.closed") },
  };
  const { data: threads, isLoading, isError, refetch } = useThreads(applicationId);
  const { data: applications } = useApplications();
  const createThread = useCreateThread();
  const [localState, setLocalState] = useState<ThreadListState>({ ...emptyThreadListState, selectedAppId: applicationId ?? "" });
  const { showNew, selectedThread, newSubject, newBody, selectedAppId, drafts } = state ?? localState;
  const setState = onStateChange ?? setLocalState;
  const setShowNew = (value: boolean) => setState((prev) => ({ ...prev, showNew: value }));
  const setSelectedThread = (value: Thread | null) => setState((prev) => ({ ...prev, selectedThread: value }));
  const setNewSubject = (value: string) => setState((prev) => ({ ...prev, newSubject: value }));
  const setNewBody = (value: string) => setState((prev) => ({ ...prev, newBody: value }));
  const setSelectedAppId = (value: string) => setState((prev) => ({ ...prev, selectedAppId: value }));
  const [createError, setCreateError] = useState(false);
  const [pendingThreadId, setPendingThreadId] = useState(initialThreadId);
  useEffect(() => {
    const thread = threads?.find((item) => item.id === pendingThreadId);
    if (thread) {
      setState((prev) => ({ ...prev, selectedThread: thread }));
      setPendingThreadId(undefined);
      onThreadOpened?.();
    }
  }, [threads, pendingThreadId, setState, onThreadOpened]);

  const handleCreate = async () => {
    if (!newSubject.trim() || !newBody.trim()) return;
    setCreateError(false);
    try {
      const thread = await createThread.mutateAsync({
        subject: newSubject,
        applicationId: selectedAppId || undefined,
        body: newBody,
      });
      setShowNew(false);
      setNewSubject("");
      setNewBody("");
      setSelectedAppId(applicationId ?? "");
      setSelectedThread(thread);
    } catch { setCreateError(true); }
  };

  if (selectedThread) {
    return <ThreadView thread={threads?.find((thread) => thread.id === selectedThread.id) ?? selectedThread} onBack={() => setSelectedThread(null)}
      draft={drafts[selectedThread.id] ?? { body: "", pendingFiles: [] }}
      onDraftChange={(draft) => setState((prev) => ({ ...prev, drafts: { ...prev.drafts, [selectedThread.id]: draft } }))} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className={`${hideHeading ? "sr-only" : ""} text-lg font-heading font-bold text-foreground flex items-center gap-2`}>
          <MessageCircle className="h-5 w-5 text-primary" />
          {t("dashboard.messages")}
        </h2>
        <Button size="sm" className="gap-2" onClick={() => setShowNew(!showNew)}>
          <MessageSquarePlus className="h-4 w-4" />
          {t("dashboard.newThread")}
        </Button>
      </div>

      {showNew && (
        <div className="bg-card border border-border rounded-xl p-4 space-y-3">
          <Input
            placeholder={t("dashboard.newThreadSubject")}
            value={newSubject}
            onChange={(e) => setNewSubject(e.target.value)}
          />
          {!applicationId && applications && applications.length > 0 && (
            <select
              value={selectedAppId}
              onChange={(e) => setSelectedAppId(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">{t("dashboard.generalInquiry")}</option>
              {applications.map((app) => (
                <option key={app.id} value={app.id}>
                  {app.membership_package.charAt(0).toUpperCase() + app.membership_package.slice(1)} — {app.status.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          )}
          <Textarea
            placeholder={t("dashboard.newThreadBody")}
            value={newBody}
            onChange={(e) => setNewBody(e.target.value)}
            rows={3}
          />
          {createError && <p role="alert" className="text-sm text-destructive">Unable to send your message. Please try again.</p>}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => setShowNew(false)}>{t("common.close")}</Button>
            <Button size="sm" onClick={handleCreate} disabled={createThread.isPending || !newSubject.trim() || !newBody.trim()}>
              {createThread.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.submit")}
            </Button>
          </div>
        </div>
      )}

      {isError ? (
        <div role="alert" className="text-sm text-destructive">Unable to load messages. <Button variant="link" onClick={() => refetch()}>Retry</Button></div>
      ) : isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : !threads?.length ? (
        <div className="bg-card border border-border rounded-xl p-8 text-center">
          <MessageCircle className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">{t("dashboard.noMessages")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {threads.map((thread) => {
            const badge = STATUS_BADGE[thread.status] || STATUS_BADGE.open;
            const BadgeIcon = badge.icon;
            return (
              <button
                key={thread.id}
                onClick={() => setSelectedThread(thread)}
                className="w-full flex items-center gap-3 p-4 bg-card border border-border rounded-xl text-left hover:border-accent/50 transition-colors"
              >
                <MessageCircle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-foreground truncate">{thread.subject}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(thread.updated_at).toLocaleDateString()} · {new Date(thread.updated_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
                <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${badge.className}`}>
                  <BadgeIcon className="h-3 w-3" />
                  {badge.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ThreadList;
